import type { Component } from "solid-js";
import { createSignal, Show } from "solid-js";
import Draggable from "../../pages/customer/dnd/Draggable";
import { IconButton, TextButton } from "../button/Button";
import Handlebar from "../field/Handlebar";
import { isNil } from "lodash-es";
import DropIndicator from "../../pages/customer/dnd/DropIndicator";
import type { IconName } from "../icon/Icon";
import { RemoteImage } from "../image/Image";
import { DECORATIVE_IMAGE_ALT } from "../../constants";
import { useModalManager } from "../modal/ModalManager";
import MediaModal from "../modal/MediaModal";
import type { CustomerResource, PendingCustomerResource } from "../../services/CustomerResourceService";
import CustomerResourceService from "../../services/CustomerResourceService";
import { useTranslation } from "../../state/i18n";

type RootOptionPropertyProps = {
  size: "sm" | "md";
  hasError: boolean;
  id: string;
  index: number;
  isLast: boolean;
  action: {
    ariaLabel: string;
    icon: IconName;
    onClick: () => void;
  };
  Input: Component;
  Preview: Component;
};

const RootOptionProperty = (props: RootOptionPropertyProps) => {
  let dragHandle!: HTMLDivElement;

  return (
    <Draggable
      data={{
        type: "OPTION",
        id: props.id,
        index: props.index,
      }}
      isLast={props.isLast}
      dragHandle={() => dragHandle}
      useDragHandleAsDragElement
      preview={props.Preview}
    >
      {(state) => (
        <>
          <div
            class="flex w-full items-center gap-x-2 rounded-sm border bg-surface-default py-0.75 pr-1 pl-2 shadow-elevation-light-1"
            classList={{
              "h-9.5": props.size === "sm",
              "h-14": props.size === "md",
              "border-error": props.hasError,
              "border-default": !props.hasError,
            }}
          >
            <div
              ref={dragHandle}
              classList={{
                "cursor-grab": state().type === "idle",
                "cursor-grabbing": state().type === "is-dragging",
              }}
            >
              <Handlebar expandDragArea />
            </div>
            <props.Input />
            <IconButton
              aria-label={props.action.ariaLabel}
              size="medium"
              icon={props.action.icon}
              variant="tertiary"
              onClick={() => props.action.onClick()}
            />
          </div>
          <Show when={state().type === "is-dragging-over" && !isNil(state().closestEdge)}>
            <DropIndicator edge={state().closestEdge!} gap={4} />
          </Show>
        </>
      )}
    </Draggable>
  );
};

export type TextOptionPropertyProps = {
  value: string;
  onChange: (value: string) => void;
} & Omit<RootOptionPropertyProps, "Input" | "Preview" | "size">;

export const TextOptionProperty = (props: TextOptionPropertyProps) => {
  const { t } = useTranslation();

  return (
    <RootOptionProperty
      size="sm"
      id={props.id}
      hasError={props.hasError}
      index={props.index}
      isLast={props.isLast}
      action={props.action}
      Preview={() => (
        <div class="flex h-9.5 w-70 min-w-0 items-center gap-x-2 rounded-sm border border-default bg-surface-default py-1 pr-1 pl-2 shadow-elevation-light-3">
          <Handlebar expandDragArea />
          <span class="truncate px-1.5 py-1 text-base-regular text-primary">{props.value}</span>
        </div>
      )}
      Input={() => (
        <input
          type="text"
          aria-label={t(($) => $.FIELD_PROPERTY_OPTIONS_LABEL_ARIA)}
          value={props.value}
          onChange={(e) => props.onChange(e.currentTarget.value)}
          class="pointer-events-auto flex size-full items-center truncate rounded-sm bg-surface-extra-subtle px-1.5 text-base-regular text-primary hover:bg-surface-extra-subtle-hover focus-visible:outline-default"
        />
      )}
    />
  );
};

export type ImageOptionPropertyProps = {
  resourceId: string | undefined | null;
  alt: string | undefined;
  onChange: (resourceId: string) => void;
} & Omit<RootOptionPropertyProps, "Input" | "Preview" | "size">;

export const ImageOptionProperty = (props: ImageOptionPropertyProps) => {
  const { t } = useTranslation();
  const { open } = useModalManager();

  const [previousValue, setPreviousValue] = createSignal<string | undefined | null>(undefined);
  const [progress, setProgress] = createSignal<number>(0);
  const [abortController, setAbortController] = createSignal<AbortController | undefined>(undefined);

  const onFileSelected = async (file: PendingCustomerResource) =>
    CustomerResourceService.upload(
      file,
      ({ progress }) => setProgress(!isNil(progress) ? progress * 100 : 0),
      setAbortController(new AbortController()).signal,
    );

  const onDelete = async (file: CustomerResource) => CustomerResourceService.delete(file.id);

  const onCancel = () => {
    const previous = previousValue();
    if (!isNil(previous)) {
      props.onChange(previous);
      setPreviousValue(undefined);
    }
    abortController()?.abort();
  };

  const openMediaModal = () => {
    setPreviousValue(props.resourceId);
    open({
      content: () => (
        <MediaModal
          progress={progress()}
          setProgress={setProgress}
          onDelete={onDelete}
          onCancel={onCancel}
          fetchPage={(page, query) => CustomerResourceService.fetchPage(page, { query })}
          mediaUpload={{ onFileSelected }}
          onSelect={(file) => props.onChange(file.id)}
          defaultTab="library"
        />
      ),
    });
  };

  return (
    <RootOptionProperty
      size="md"
      id={props.id}
      hasError={props.hasError}
      index={props.index}
      isLast={props.isLast}
      action={props.action}
      Preview={() => (
        <div class="flex h-9.5 w-70 min-w-0 items-center gap-x-2 rounded-sm border border-default bg-surface-default py-0.75 pr-1 pl-2 shadow-elevation-light-3">
          <Handlebar expandDragArea />
          <Show when={props.resourceId}>
            {(value) => (
              <RemoteImage alt={props.alt ?? DECORATIVE_IMAGE_ALT} cover={false} size="xs" resourceId={value()} />
            )}
          </Show>
        </div>
      )}
      Input={() => (
        <Show
          when={props.resourceId}
          fallback={
            <div class="flex size-full items-center justify-center rounded-sm bg-surface-extra-subtle">
              <TextButton
                text={t(($) => $.FIELD_PROPERTY_OPTIONS_SELECT_IMAGE)}
                onClick={openMediaModal}
                variant="secondary"
                size="medium"
              />
            </div>
          }
        >
          {(value) => (
            <div class="group relative size-full">
              <RemoteImage alt={props.alt ?? DECORATIVE_IMAGE_ALT} cover={false} size="sm" resourceId={value()} />
              <div class="absolute inset-0 hidden size-full items-center justify-center rounded-sm group-hover:flex group-hover:bg-overlay-default">
                <TextButton
                  text={t(($) => $.FIELD_PROPERTY_OPTIONS_CHANGE_IMAGE)}
                  onClick={openMediaModal}
                  variant="secondary"
                  size="medium"
                />
              </div>
            </div>
          )}
        </Show>
      )}
    />
  );
};
